import React from "react";
import { Box, Button, LinearProgress, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";
import { formatPeriodRange, isLifetimeTaskQuota, resolvePlanQuota } from "../../features/agents/billingPeriod";

const useStyles = makeStyles((theme) => ({
  barOk: {
    backgroundColor: theme.palette.primary.main,
  },
  barWarn: {
    backgroundColor: "#ed6c02",
  },
  barOver: {
    backgroundColor: theme.palette.error.main,
  },
  track: {
    height: 8,
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
  },
}));

const meterPaperStyle = { padding: 16, marginBottom: 16 };

/** Reserves the same footprint as the loaded meter so agent cards don't jump. */
export const AgentUsageMeterSkeleton = () => (
  <Paper variant="outlined" style={meterPaperStyle} aria-busy="true" aria-label="Loading agent task usage">
    <Box display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" style={{ gap: 8 }}>
      <Skeleton animation="wave" variant="text" width={140} height={24} />
      <Skeleton animation="wave" variant="text" width={72} height={24} />
    </Box>
    <Box mt={1} mb={1}>
      <Skeleton animation="wave" variant="rect" height={8} style={{ borderRadius: 6 }} />
    </Box>
    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
      <Skeleton animation="wave" variant="text" width="70%" height={20} style={{ maxWidth: 520 }} />
      <Skeleton animation="wave" variant="text" width={96} height={28} />
    </Box>
  </Paper>
);

const AgentUsageMeter = ({ usage, onViewDetails }) => {
  const classes = useStyles();
  const tenant = usage?.tenant || null;
  const used = Number(usage?.live_total ?? usage?.liveTotal ?? 0) || 0;
  const quota = Number.isFinite(Number(usage?.quota)) ? Number(usage.quota) : resolvePlanQuota(tenant);
  const pctRaw = quota > 0 ? (used / quota) * 100 : 0;
  const pct = Math.min(100, used > 0 ? Math.max(2, pctRaw) : 0);
  const lifetime = usage?.lifetime === true || isLifetimeTaskQuota(tenant);
  const days = lifetime ? null : usage?.daysUntilReset;
  const periodStart = !lifetime && usage?.periodStart ? new Date(usage.periodStart) : null;
  const periodEnd = !lifetime && usage?.periodEnd ? new Date(usage.periodEnd) : null;
  const periodLabel =
    periodStart && periodEnd && !Number.isNaN(periodStart.getTime())
      ? formatPeriodRange(periodStart, periodEnd)
      : "";
  const planName = tenant?.plan ? String(tenant.plan) : "plan";
  const barClass = pctRaw >= 100 ? classes.barOver : pctRaw >= 80 ? classes.barWarn : classes.barOk;
  const caption = lifetime
    ? `Free plan lifetime credits. ${quota.toLocaleString()} total. They do not reset each month. Upgrade to a paid plan after they are used. Preview and test runs are excluded.`
    : `Live agent tasks this billing period${periodLabel ? ` (${periodLabel})` : ""}. ${planName} quota${
        typeof days === "number" ? ` · resets in ${days} day${days === 1 ? "" : "s"}` : ""
      }. Preview and test runs are excluded.`;

  return (
    <Paper variant="outlined" style={meterPaperStyle}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" style={{ gap: 8 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          Agent task usage
        </Typography>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          {used.toLocaleString()} / {quota.toLocaleString()}
        </Typography>
      </Box>
      <Box mt={1} mb={1}>
        <LinearProgress
          variant="determinate"
          value={pct}
          className={classes.track}
          classes={{ bar: barClass }}
        />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
        <Typography variant="caption" color="textSecondary">
          {caption}
        </Typography>
        {onViewDetails ? (
          <Button color="primary" size="small" onClick={onViewDetails} style={{ textTransform: "none" }}>
            View by agent
          </Button>
        ) : null}
      </Box>
    </Paper>
  );
};

export default AgentUsageMeter;
